import { useEffect, useState } from "react";
import { Plus, Trash2, Download, QrCode, Copy } from "lucide-react";
import api, { API, downloadFile, apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function AdminMarketing() {
  const [tab, setTab] = useState("promos");
  const [promos, setPromos] = useState([]);
  const [subs, setSubs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [products, setProducts] = useState([]);
  const [qr, setQr] = useState({ slug: "", size: "" });
  const [f, setF] = useState({ code: "", type: "percentage", value: 10, usage_limit: "", expires_at: "" });

  const load = () => {
    api.get("/admin/promos").then(({ data }) => setPromos(data)).catch(() => {});
    api.get("/admin/newsletter").then(({ data }) => setSubs(data)).catch(() => {});
    api.get("/admin/stock-alerts").then(({ data }) => setAlerts(data)).catch(() => {});
    api.get("/products", { params: { admin: true } }).then(({ data }) => setProducts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createPromo = async () => {
    if (!f.code) return;
    try { await api.post("/admin/promos", { ...f, value: parseFloat(f.value), usage_limit: f.usage_limit ? parseInt(f.usage_limit) : null, expires_at: f.expires_at || null }); setF({ code: "", type: "percentage", value: 10, usage_limit: "", expires_at: "" }); load(); toast.success("Promo created."); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delPromo = async (p) => { await api.delete(`/admin/promos/${p.id}`); load(); };

  const qrProduct = products.find((p) => p.slug === qr.slug);
  const buyUrl = qrProduct ? `${window.location.origin}/product/${qrProduct.slug}${qr.size ? `?buy=${encodeURIComponent(qr.size)}` : ""}` : "";
  const qrSrc = buyUrl ? `${API}/qr?data=${encodeURIComponent(buyUrl)}` : "";
  const copyUrl = () => { navigator.clipboard.writeText(buyUrl); toast.success("Buy link copied."); };

  const TABS = [["promos", "Promo Codes"], ["subscribers", "Newsletter"], ["alerts", "Back-in-Stock"], ["qr", "QR & Links"]];

  return (
    <div data-testid="admin-marketing">
      <h1 className="font-heading text-4xl tracking-tight mb-2">Marketing</h1>
      <p className="font-body text-sm text-ink/60 mb-6">Discount engine, newsletter list and back-in-stock demand.</p>

      <div className="flex gap-4 border-b border-ink/10 mb-8">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`marketing-tab-${k}`} className={`font-body uppercase tracking-wide2 text-[0.65rem] pb-3 border-b-2 transition-colors ${tab === k ? "border-ink text-ink" : "border-transparent text-ink/50 hover:text-ink"}`}>{l}</button>
        ))}
      </div>

      {tab === "promos" && (
        <>
          <div className="adm-card mb-6 grid md:grid-cols-6 gap-3 items-end">
            <div><label className="adm-label">Code</label><input className="adm-input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" /></div>
            <div><label className="adm-label">Type</label><select className="adm-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}><option value="percentage">Percent</option><option value="fixed">Fixed $</option></select></div>
            <div><label className="adm-label">Value</label><input className="adm-input" type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="promo-value-input" /></div>
            <div><label className="adm-label">Usage Limit</label><input className="adm-input" type="number" value={f.usage_limit} onChange={(e) => setF({ ...f, usage_limit: e.target.value })} /></div>
            <div><label className="adm-label">Expires</label><input className="adm-input" type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} /></div>
            <button onClick={createPromo} className="adm-btn" data-testid="create-promo-btn"><Plus size={13} /> Create</button>
          </div>
          <div className="adm-card p-0 overflow-hidden">
            <table className="w-full text-left font-body text-sm">
              <thead className="bg-cream"><tr className="uppercase tracking-wide2 text-[0.6rem] text-ink/60"><th className="px-5 py-3">Code</th><th className="px-5 py-3">Discount</th><th className="px-5 py-3">Used</th><th className="px-5 py-3">Limit</th><th className="px-5 py-3">Expires</th><th className="px-5 py-3"></th></tr></thead>
              <tbody>{promos.map((p) => (
                <tr key={p.id} className="border-t border-ink/5" data-testid={`promo-row-${p.code}`}>
                  <td className="px-5 py-3 font-medium">{p.code}</td>
                  <td className="px-5 py-3">{p.type === "percentage" ? `${p.value}%` : `$${p.value}`}</td>
                  <td className="px-5 py-3">{p.used || 0}</td><td className="px-5 py-3">{p.usage_limit || "∞"}</td>
                  <td className="px-5 py-3 text-ink/60">{p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="px-5 py-3 text-right"><button onClick={() => delPromo(p)} className="icon-thin text-destructive"><Trash2 size={14} /></button></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </>
      )}

      {tab === "subscribers" && (
        <>
          <div className="flex justify-between items-center mb-4"><p className="font-body text-sm text-ink/60">{subs.length} subscriber(s)</p><button onClick={() => downloadFile("/admin/newsletter/export", "subscribers.csv")} className="adm-btn-outline" data-testid="export-subs-btn"><Download size={13} /> Export CSV</button></div>
          <div className="adm-card p-0 overflow-hidden">
            <table className="w-full text-left font-body text-sm">
              <thead className="bg-cream"><tr className="uppercase tracking-wide2 text-[0.6rem] text-ink/60"><th className="px-5 py-3">Email</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Source</th><th className="px-5 py-3">Date</th></tr></thead>
              <tbody>{subs.map((s) => (
                <tr key={s.id} className="border-t border-ink/5"><td className="px-5 py-3">{s.email}</td><td className="px-5 py-3 text-ink/60">{s.name || "—"}</td><td className="px-5 py-3">{s.source}</td><td className="px-5 py-3 text-ink/60">{new Date(s.created_at).toLocaleDateString()}</td></tr>
              ))}
              {subs.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-ink/40">No subscribers yet.</td></tr>}</tbody>
            </table>
          </div>
        </>
      )}

      {tab === "alerts" && (
        <div className="adm-card p-0 overflow-hidden">
          <table className="w-full text-left font-body text-sm">
            <thead className="bg-cream"><tr className="uppercase tracking-wide2 text-[0.6rem] text-ink/60"><th className="px-5 py-3">Email</th><th className="px-5 py-3">Product</th><th className="px-5 py-3">Size</th><th className="px-5 py-3">Date</th></tr></thead>
            <tbody>{alerts.map((a) => (
              <tr key={a.id} className="border-t border-ink/5"><td className="px-5 py-3">{a.email}</td><td className="px-5 py-3">{a.product_title}</td><td className="px-5 py-3">{a.size}</td><td className="px-5 py-3 text-ink/60">{new Date(a.created_at).toLocaleDateString()}</td></tr>
            ))}
            {alerts.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-ink/40">No back-in-stock requests yet.</td></tr>}</tbody>
          </table>
        </div>
      )}

      {tab === "qr" && (
        <div className="max-w-2xl" data-testid="qr-links">
          <div className="bg-warm/30 p-3 font-body text-[0.7rem] mb-6">Generate a 1-click direct-buy link and QR code for any piece. Scanning adds it straight to the shopper's bag.</div>
          <div className="adm-card grid md:grid-cols-2 gap-4 mb-6">
            <div><label className="adm-label">Product</label>
              <select className="adm-input" value={qr.slug} onChange={(e) => setQr({ slug: e.target.value, size: "" })} data-testid="qr-product-select">
                <option value="">Select a piece…</option>
                {products.map((p) => <option key={p.id} value={p.slug}>{p.title}</option>)}
              </select>
            </div>
            <div><label className="adm-label">Size (optional)</label>
              <select className="adm-input" value={qr.size} onChange={(e) => setQr({ ...qr, size: e.target.value })} disabled={!qrProduct} data-testid="qr-size-select">
                <option value="">Any / let shopper choose</option>
                {(qrProduct?.variants || []).map((v) => <option key={v.size} value={v.size}>{v.size}{v.stock <= 0 ? " (sold out)" : ""}</option>)}
              </select>
            </div>
          </div>

          {buyUrl && (
            <div className="adm-card flex flex-col md:flex-row items-center gap-6" data-testid="qr-result">
              <img src={qrSrc} alt="Direct buy QR" className="w-40 h-40 border border-ink/10 bg-white" data-testid="qr-image" />
              <div className="flex-1 w-full">
                <label className="adm-label">Direct-Buy Link</label>
                <div className="flex gap-2 mb-4">
                  <input readOnly value={buyUrl} className="adm-input flex-1 text-xs" data-testid="qr-buy-url" onFocus={(e) => e.target.select()} />
                  <button onClick={copyUrl} className="adm-btn-outline" data-testid="qr-copy-btn"><Copy size={13} /></button>
                </div>
                <a href={qrSrc} download={`${qr.slug}${qr.size ? `-${qr.size}` : ""}-qr.png`} className="adm-btn" data-testid="qr-download-btn"><Download size={13} /> Download QR PNG</a>
              </div>
            </div>
          )}
          {!buyUrl && <p className="font-body text-sm text-ink/40 text-center py-10 flex items-center justify-center gap-2"><QrCode size={16} /> Select a piece to generate its buy link.</p>}
        </div>
      )}
    </div>
  );
}
